import { DEFAULT_FORMAT_FALLBACK } from './constants';
import { FormatDateOptions, FormatDateVariant } from './types';

const resolveDateValue = (
  value: Date | number | string | null | undefined,
  isDateOnly?: boolean
): Date | null => {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (isDateOnly || /^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);

      if (match) {
        const [, year, month, day] = match;
        const utcDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

        return Number.isNaN(utcDate.getTime()) ? null : utcDate;
      }
    }
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeShortMonthLabel = (value: string): string => {
  return value.replace(/\.(?=,|$)/g, '');
};

const getDateTimeFormatOptions = (
  variant: FormatDateVariant,
  timeZone?: string
): Intl.DateTimeFormatOptions => {
  const resolvedTimeZone = timeZone ?? (variant === 'date' ? 'UTC' : undefined);

  if (variant === 'date') {
    return {
      day: '2-digit',
      month: '2-digit',
      timeZone: resolvedTimeZone,
      year: 'numeric'
    };
  }

  if (variant === 'shortDate') {
    return {
      day: 'numeric',
      month: 'short',
      timeZone: resolvedTimeZone
    };
  }

  if (variant === 'time') {
    return {
      hour: '2-digit',
      hour12: false,
      minute: '2-digit',
      timeZone: resolvedTimeZone
    };
  }

  if (variant === 'timeWithSeconds') {
    return {
      hour: '2-digit',
      hour12: false,
      minute: '2-digit',
      second: '2-digit',
      timeZone: resolvedTimeZone
    };
  }

  return {
    day: 'numeric',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: 'short',
    timeZone: resolvedTimeZone
  };
};

export const formatDate = (options: FormatDateOptions): string => {
  const {
    fallback = DEFAULT_FORMAT_FALLBACK,
    isDateOnly,
    timeZone,
    value,
    variant = 'date'
  } = options;

  const date = resolveDateValue(value, isDateOnly);

  if (!date) return fallback;

  const isDateOnlyValue = Boolean(
    isDateOnly || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim()))
  );
  const resolvedTimeZone = isDateOnlyValue ? 'UTC' : timeZone;

  const formattedValue = new Intl.DateTimeFormat(
    'ru-RU',
    getDateTimeFormatOptions(variant, resolvedTimeZone)
  ).format(date);

  return variant === 'shortDate' || variant === 'dateTime'
    ? normalizeShortMonthLabel(formattedValue)
    : formattedValue;
};